import { useEffect, useRef, useState } from "react";
import { LuPlus, LuSearch, LuX } from "react-icons/lu";
import { getCategories } from "../../../../../api/categories";
import { getCatalogs } from "../../../../../api/catalogs";
import { getSalesChannels } from "../../../../../api/salesChannels";
import {
  updateCatalogsToProduct,
  updateCategoriesToProduct,
} from "../../../../../api/products";
import { EditorDialog } from "./editorDialog";

const ids = (rows, key) => (rows || []).map((row) => row[key]);
const equal = (a, b) => [...a].sort().join(",") === [...b].sort().join(",");
export const ReferenceOrganization = ({
  data,
  registerSave,
  onDirty,
  disabled,
  allergens,
}) => {
  const [categories, setCategories] = useState(
    ids(data?.Categories, "categoryId"),
  );
  const [catalogs, setCatalogs] = useState(ids(data?.Catalogs, "catalogId"));
  const saved = useRef({ categories, catalogs });
  const [available, setAvailable] = useState({
    categories: [],
    catalogs: [],
    channels: [],
  });
  const [loading, setLoading] = useState(true),
    [error, setError] = useState(""),
    [attempt, setAttempt] = useState(0);
  const [picker, setPicker] = useState(null),
    [search, setSearch] = useState(""),
    [selection, setSelection] = useState([]);
  useEffect(() => {
    setCategories((previous) => {
      if (!equal(previous, saved.current.categories)) return previous;
      const next = ids(data?.Categories, "categoryId");
      saved.current.categories = next;
      return next;
    });
    setCatalogs((previous) => {
      if (!equal(previous, saved.current.catalogs)) return previous;
      const next = ids(data?.Catalogs, "catalogId");
      saved.current.catalogs = next;
      return next;
    });
  }, [data?.Categories, data?.Catalogs]);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([getCategories(), getCatalogs(), getSalesChannels()])
      .then(([c, k, s]) => {
        if (!active) return;
        if (c?.error !== false || k?.error !== false || s?.error !== false)
          throw Error("load");
        setAvailable({
          categories: c.data || [],
          catalogs: k.data || [],
          channels: s.data || [],
        });
      })
      .catch(() => {
        if (active) setError("No se pudo cargar la organización del producto.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [attempt]);
  useEffect(
    () =>
      registerSave("organization", async (productId) => {
        for (const [key, selected, save] of [
          ["categories", categories, updateCategoriesToProduct],
          ["catalogs", catalogs, updateCatalogsToProduct],
        ]) {
          if (equal(selected, saved.current[key])) continue;
          const response = await save(productId, selected);
          if (response?.error !== false)
            throw Error(
              `No se pudieron guardar ${key === "categories" ? "las categorías" : "los catálogos"}. Tus selecciones se conservan; vuelve a guardar.`,
            );
          saved.current[key] = [...selected];
        }
      }),
    [categories, catalogs, registerSave],
  );
  const change = (key, value) => {
    (key === "categories" ? setCategories : setCatalogs)(value);
    onDirty();
  };
  const open = (key) => {
    setPicker(key);
    setSelection([...(key === "categories" ? categories : catalogs)]);
    setSearch("");
  };
  const selectedRows = (key, selected, idKey) => {
    const merged = [
      ...available[key],
      ...(
        data?.[key === "categories" ? "Categories" : "Catalogs"] || []
      ).filter((row) => !available[key].some((a) => a[idKey] === row[idKey])),
    ];
    return merged.filter((row) => selected.includes(row[idKey]));
  };
  const pickerKey = picker === "categories" ? "categoryId" : "catalogId";
  return (
    <section className="productFormCard referenceOrganization">
      <header>
        <h2>Organización del producto</h2>
        <p>Categorías, catálogos, canales de venta y alérgenos.</p>
      </header>
      <div className="referenceOrganizationGrid">
        {["categories", "catalogs"].map((key, index) => {
          const title = index ? "Catálogos" : "Categorías",
            selected = index ? catalogs : categories,
            idKey = index ? "catalogId" : "categoryId";
          return (
            <section key={key}>
              <h3>{title}</h3>
              <p>
                {index
                  ? "Colecciones que se publican juntas en cada canal."
                  : "Dónde aparece el producto dentro de sus catálogos."}
              </p>
              <button
                type="button"
                className="referenceOutline"
                disabled={disabled || loading || !!error}
                onClick={() => open(key)}
              >
                <LuPlus />
                Agregar {index ? "catálogo" : "categorías"}
              </button>
              <div className={index ? "referenceCatalogs" : "referenceChips"}>
                {selectedRows(key, selected, idKey).map((row) => (
                  <div
                    key={row[idKey]}
                    className={index ? "referenceCatalog" : "referenceChip"}
                  >
                    <span>
                      <strong>{row.name}</strong>
                      {index === 1 && row.description && (
                        <small>{row.description}</small>
                      )}
                    </span>
                    <button
                      type="button"
                      aria-label={`Quitar ${row.name}`}
                      disabled={disabled}
                      onClick={() =>
                        change(
                          key,
                          selected.filter((id) => id !== row[idKey]),
                        )
                      }
                    >
                      <LuX />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          );
        })}
        <section>
          <h3>Canales de venta</h3>
          <p>Disponibilidad según los catálogos asociados a cada canal.</p>
          {loading ? (
            <p role="status">Cargando canales…</p>
          ) : (
            <div className="referenceChannelTable">
              <table>
                <thead>
                  <tr>
                    <th>Canal de venta</th>
                    <th>Disponible</th>
                  </tr>
                </thead>
                <tbody>
                  {available.channels.map((channel) => {
                    const channelCatalogs =
                      channel.Catalogs ||
                      channel.catalogs ||
                      (channel.catalogId
                        ? [{ catalogId: channel.catalogId }]
                        : channel.Catalog
                          ? [channel.Catalog]
                          : []);
                    const included = channelCatalogs.some((c) =>
                      catalogs.includes(c.catalogId),
                    );
                    return (
                      <tr key={channel.salesChannelId}>
                        <th scope="row">{channel.name}</th>
                        <td>
                          {channelCatalogs.length
                            ? included && channel.isActive !== false
                              ? "En catálogo"
                              : "Sin asignar"
                            : "Sin catálogo"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!available.channels.length && (
                <p>No hay canales de venta configurados.</p>
              )}
            </div>
          )}
          <small className="referenceHelp">
            Los canales se configuran desde Canal de venta. El precio base del
            producto se conserva.
          </small>
        </section>
        <section className="referenceAllergens">
          <h3>Alérgenos</h3>
          <p>Información del producto para la tienda online.</p>
          {allergens}
        </section>
        {error && (
          <div role="alert">
            <p>{error}</p>
            <button type="button" onClick={() => setAttempt((v) => v + 1)}>
              Reintentar
            </button>
          </div>
        )}
      </div>
      {picker && (
        <EditorDialog
          title={
            picker === "categories" ? "Agregar categorías" : "Agregar catálogo"
          }
          description={
            picker === "categories"
              ? "Un producto puede estar en varias categorías."
              : "Un producto puede publicarse en varios catálogos."
          }
          onClose={() => setPicker(null)}
          footer={
            <>
              <button
                type="button"
                className="referenceOutline"
                onClick={() => setPicker(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="referencePrimary"
                onClick={() => {
                  change(picker, selection);
                  setPicker(null);
                }}
              >
                Seleccionar
              </button>
            </>
          }
        >
          <label className="referenceSearch">
            <LuSearch />
            <input
              type="search"
              aria-label="Buscar"
              placeholder="Buscar"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="referencePickerOptions">
            {available[picker]
              .filter((row) =>
                `${row.name} ${row.description || ""}`
                  .toLocaleLowerCase("es")
                  .includes(search.toLocaleLowerCase("es")),
              )
              .map((row) => (
                <label key={row[pickerKey]}>
                  <input
                    type="checkbox"
                    checked={selection.includes(row[pickerKey])}
                    onChange={() =>
                      setSelection((value) =>
                        value.includes(row[pickerKey])
                          ? value.filter((id) => id !== row[pickerKey])
                          : [...value, row[pickerKey]],
                      )
                    }
                  />
                  <span>
                    <strong>{row.name}</strong>
                    {row.description && <small>{row.description}</small>}
                    {row.isActive === false && <small>Inactivo</small>}
                  </span>
                </label>
              ))}
          </div>
          {!available[picker].some((row) =>
            `${row.name} ${row.description || ""}`
              .toLocaleLowerCase("es")
              .includes(search.toLocaleLowerCase("es")),
          ) && <p>No hay coincidencias.</p>}
        </EditorDialog>
      )}
    </section>
  );
};
